import { createFileRoute, Link } from '@tanstack/react-router';
import { CodeBlock, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';

export const Route = createFileRoute('/documentation/overview')({
  component: OverviewPage,
});

function OverviewPage() {
  return (
    <>
      <div className={styles.breadcrumb}>Getting Started</div>
      <h1 className={styles.pageTitle}>Interlucent Documentation</h1>
      <p className={styles.pageSubtitle}>
        Stream GPU-rendered applications to any browser. Build interactive
        experiences without managing infrastructure.
      </p>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="what-is-interlucent">
          What is Interlucent
        </h2>
        <p className={styles.prose}>
          Interlucent connects your GPU-rendered applications to end users in
          the browser via low-latency WebRTC streams. You upload an
          application, configure how sessions behave, and the platform handles
          connection setup and media delivery — from server to screen.
        </p>
        <p className={styles.prose}>
          The system operates through{' '}
          <span className={styles.emphasis}>admission tokens</span> — signed JWTs
          that declare the maximum scope of what a session participant is
          permitted to do. At connection time, the platform validates, refines,
          and enforces these constraints.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="quickstart">
          Quickstart
        </h2>
        <p className={styles.prose}>
          The fastest path to a live stream is a single HTML element. Pass an
          admission token and the component handles the rest.
        </p>
        <CodeBlock language="html" title="quickstart.html">
{`<pixel-stream></pixel-stream>

<script src="https://cdn.interlucent.ai/dev/pixel-stream/0.0.75/pixel-stream.iife.min.js"></script>
<script>
  const ps = document.querySelector('pixel-stream');
  ps.admissionToken = 'YOUR_TOKEN';
</script>`}
        </CodeBlock>
        <p className={styles.prose}>
          Ready to go deeper?{' '}
          <Link to="/documentation/streaming-guide">
            The Streaming Guide
          </Link>{' '}
          walks you through everything from this one-line embed to custom
          overlays and bidirectional messaging.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="core-concepts">
          Core Concepts
        </h2>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Project</div>
            <div className={styles.conceptDef}>
              A container for applications, profiles, and configuration. Each
              project belongs to an organization and has its own signing keys.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Application</div>
            <div className={styles.conceptDef}>
              A versioned binary artifact (game, 3D experience, simulation)
              uploaded to the platform. Applications are deployed to GPU
              workers and streamed to browsers.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Session</div>
            <div className={styles.conceptDef}>
              A live connection between a browser and a GPU worker. Sessions
              carry video, audio, and data channels over WebRTC.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Admission Token</div>
            <div className={styles.conceptDef}>
              A signed JWT created by your backend that declares what a session
              is allowed to do — which application to stream, how long to wait,
              and how resilient the connection should be.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Streaming</div>
            <div className={styles.conceptDef}>
              The real-time delivery of video, audio, and input between the
              browser and the GPU worker. Powered by WebRTC with built-in
              reconnection and recovery.
            </div>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="how-it-works">
          How It Works
        </h2>
        <p className={styles.prose}>
          The connection flow follows four steps from your backend to the browser.
        </p>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <span>
              <span className={styles.emphasis}>Create an admission token</span>
              {' '} — Your backend uses the SDK to sign a token declaring the
              application, tolerances, and tracking reference.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <span>
              <span className={styles.emphasis}>Connect</span>
              {' '} — The browser passes the admission token to the{' '}
              <code>&lt;pixel-stream&gt;</code> component. The platform
              automatically validates the token, resolves the application
              version, and establishes a session.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>3</span>
            <span>
              <span className={styles.emphasis}>Match a GPU worker</span>
              {' '} — The platform provisions a GPU worker for your session and
              brokers the WebRTC connection between the browser and the worker.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>4</span>
            <span>
              <span className={styles.emphasis}>Stream</span>
              {' '} — Video, audio, and data flow directly between the worker
              and browser over a peer-to-peer WebRTC connection. The platform
              monitors session health and enforces tolerance timers.
            </span>
          </li>
        </ol>

        <InfoCallout variant="tip">
          The Swift Job Request flag (<code className={styles.mono}>sjr</code>)
          submits the GPU worker request during token exchange, shaving one
          round-trip off the connection flow. 
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="reference">
          Reference
        </h2>
        <div className={styles.linkGrid}>
          <Link to="/documentation/streaming-guide" className={styles.linkCard}>
            <div className={styles.linkCardTitle}>Streaming Guide</div>
            <div className={styles.linkCardDesc}>
              From a one-line embed to custom overlays and bidirectional
              messaging — a progressive, hands-on walkthrough.
            </div>
          </Link>
          {/* <Link to="/documentation/admission-tokens" className={styles.linkCard}>
            <div className={styles.linkCardTitle}>Tokens</div>
            <div className={styles.linkCardDesc}>
              Token schema, claims reference, and how tokens control session
              access.
            </div>
          </Link> */}
          <Link to="/documentation/tolerances" className={styles.linkCard}>
            <div className={styles.linkCardTitle}>Session Behavior</div>
            <div className={styles.linkCardDesc}>
              Timing constraints for queue wait, rendezvous, reconnection, and
              WebRTC negotiation.
            </div>
          </Link>
          <Link to="/documentation/sdk-reference" className={styles.linkCard}>
            <div className={styles.linkCardTitle}>SDK Reference</div>
            <div className={styles.linkCardDesc}>
              TypeScript examples for common token patterns — minimal,
              production, low-latency, and resilient.
            </div>
          </Link>
        </div>
      </section>
    </>
  );
}
